import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axiosInstance from '../../api/axiosInstance';
import { toast } from 'react-toastify';
import { AccessibleSelect } from '../common';
import { RtModal, RtModalField, RtModalSection } from '../ui/RtModal';
import '../../styles/rt2-plan-detail.css';

/**
 * RunTestPlanModal Component
 * Reusable modal for running test plans with options:
 * - Local Machine (via browser extension)
 * - Server (headless, via API)
 *
 * Props:
 * - isOpen: boolean - Controls modal visibility
 * - onClose: function - Called when modal closes
 * - testPlanIds: array - Array of test plan IDs to execute
 * - testPlanNames: array - Array of test plan names for display
 * - projectId: number - Project ID for context
 * - projectName: string - Project name for display
 * - environmentSettingId: number|null - Prefill from test plan's environment_setting_id
 */
const RunTestPlanModal = ({
  isOpen,
  onClose,
  testPlanIds = [],
  testPlanNames = [],
  projectId,
  projectName,
  environmentSettingId = null,
}) => {
  const [selectedMode, setSelectedMode] = useState(null);
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState('');
  const [environments, setEnvironments] = useState([]);
  const [environmentsLoading, setEnvironmentsLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchEnvironments = useCallback(async (envProjectId) => {
    if (!envProjectId) {
      setEnvironments([]);
      return;
    }

    try {
      setEnvironmentsLoading(true);
      const response = await axiosInstance.get(
        `/user_profile/list_environments?project_id=${envProjectId}`
      );

      if (response.data && response.status_code === 200) {
        setEnvironments(response.data || []);
      } else {
        console.error('Failed to fetch environments:', response);
        setEnvironments([]);
      }
    } catch (err) {
      console.error('Error fetching environments:', err);
      setEnvironments([]);
    } finally {
      setEnvironmentsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && projectId) {
      fetchEnvironments(projectId);
    }
  }, [isOpen, projectId, fetchEnvironments]);

  // Default execution mode to "Local Machine" whenever the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setSelectedMode('local');
    setError(null);
  }, [isOpen]);

  // Prefill environment from the test plan's environment_setting_id when environments load.
  useEffect(() => {
    if (!isOpen) return;
    if (selectedEnvironmentId) return;
    if (environmentsLoading) return;
    if (!environments || environments.length === 0) return;
    if (environmentSettingId == null || environmentSettingId === '') return;

    const match = environments.find(
      (env) => env.id === parseInt(environmentSettingId, 10)
    );
    if (match) {
      setSelectedEnvironmentId(String(match.id));
      setError(null);
    }
  }, [isOpen, selectedEnvironmentId, environmentSettingId, environments, environmentsLoading]);

  const selectedEnvironment = environments.find(
    (env) => env.id === parseInt(selectedEnvironmentId, 10)
  );

  const environmentOptions = useMemo(
    () =>
      environments.map((env) => ({
        value: String(env.id),
        label: env.environment_name,
      })),
    [environments]
  );

  /**
   * Handle Run button click
   * Executes test plan(s) based on selected mode
   */
  const handleRun = async () => {
    if (!selectedEnvironmentId) {
      setError('Please select an environment');
      return;
    }

    if (!selectedMode) {
      setError('Please select an execution mode');
      return;
    }

    if (!testPlanIds || testPlanIds.length === 0) {
      setError('No test plans selected');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (selectedMode === 'local') {
        window.postMessage(
          {
            source: 'ROBITEST_CLOUD',
            action: 'RUN_TEST_PLANS',
            test_plan_ids: testPlanIds,
            project_id: projectId,
            environment_id: selectedEnvironment?.id ?? parseInt(selectedEnvironmentId, 10),
            environment_url: selectedEnvironment?.target_url || '',
          },
          '*'
        );

        toast.success(
          `Running ${testPlanIds.length} test plan(s) on Local Machine`
        );
        setTimeout(() => {
          setIsLoading(false);
          resetModal();
          onClose();
        }, 500);
      } else if (selectedMode === 'server') {
        const payload = {
          test_plan_ids: testPlanIds,
          environment_id: selectedEnvironment?.id ?? parseInt(selectedEnvironmentId, 10),
        };

        const response = await axiosInstance.post(
          `/execute_file/execute_test_plans`,
          payload
        );

        if (response?.status === 202 || response?.status_code === 202) {
          toast.success(response.message || 'Test plan(s) execution started on Server');
          setIsLoading(false);
          resetModal();
          onClose();
        } else {
          throw new Error(response?.message || 'Failed to execute test plans');
        }
      }
    } catch (err) {
      console.error('Error executing test plans:', err);
      const errorMessage =
        err.response?.data?.message || err.message || 'Failed to execute test plans';
      toast.error(errorMessage);
      setIsLoading(false);
    }
  };

  /**
   * Reset modal state
   */
  const resetModal = () => {
    setSelectedMode(null);
    setSelectedEnvironmentId('');
    setError(null);
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    resetModal();
    onClose();
  };

  const displayNames =
    testPlanNames && testPlanNames.length > 0
      ? testPlanNames.length === 1
        ? testPlanNames[0]
        : `${testPlanNames.length} Test Plans`
      : 'Test Plans';

  const canRun =
    Boolean(selectedEnvironmentId) && Boolean(selectedMode) && !environmentsLoading;

  return (
    <RtModal
      open={isOpen}
      onClose={handleClose}
      title={`Run ${displayNames}`}
      subtitle={
        projectName
          ? `Project · ${projectName}`
          : 'Select environment and where to run these plans'
      }
      size="md"
      closeDisabled={isLoading}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="rt-btn rt-btn--secondary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleRun}
            disabled={isLoading || !canRun}
            className="rt-btn rt-btn--primary"
          >
            {isLoading ? 'Running…' : 'Run Test Plans'}
          </button>
        </>
      }
    >
      <RtModalSection title="Environment">
        <RtModalField
          label="Environment"
          htmlFor="runTestPlanEnvironment"
          required
          error={error && !selectedEnvironmentId ? error : null}
          hint={
            selectedEnvironment
              ? `URL: ${selectedEnvironment.target_url}`
              : selectedMode === 'local'
                ? 'Make sure the extension is installed before a local run.'
                : null
          }
        >
          <AccessibleSelect
            id="runTestPlanEnvironment"
            value={selectedEnvironmentId}
            onChange={(value) => {
              setSelectedEnvironmentId(value);
              setError(null);
            }}
            options={environmentOptions}
            placeholder={
              environmentsLoading ? 'Loading environments…' : 'Select an environment'
            }
            disabled={isLoading || environmentsLoading}
            required
            error={Boolean(error && !selectedEnvironmentId)}
            aria-labelledby="runTestPlanEnvironment-label"
          />
        </RtModalField>
      </RtModalSection>

      <RtModalSection title="Execution mode">
        <div className="rt-run-plan-field" style={{ margin: 0 }}>
          <label
            className={`rt-run-plan-mode ${
              selectedMode === 'local' ? 'rt-run-plan-mode--active' : ''
            }`}
          >
            <input
              type="radio"
              name="executionMode"
              value="local"
              checked={selectedMode === 'local'}
              onChange={(e) => {
                setSelectedMode(e.target.value);
                setError(null);
              }}
              disabled={isLoading}
            />
            <div className="rt-run-plan-mode__text">
              <p className="rt-run-plan-mode__title">Local Machine</p>
              <p className="rt-run-plan-mode__desc">
                Run via browser extension on your local machine
              </p>
            </div>
          </label>

          <label
            className={`rt-run-plan-mode ${
              selectedMode === 'server' ? 'rt-run-plan-mode--active' : ''
            }`}
          >
            <input
              type="radio"
              name="executionMode"
              value="server"
              checked={selectedMode === 'server'}
              onChange={(e) => {
                setSelectedMode(e.target.value);
                setError(null);
              }}
              disabled={isLoading}
            />
            <div className="rt-run-plan-mode__text">
              <p className="rt-run-plan-mode__title">Server (Headless Run)</p>
              <p className="rt-run-plan-mode__desc">
                Run on server infrastructure in headless mode
              </p>
            </div>
          </label>
        </div>
        {error && selectedEnvironmentId ? (
          <div className="rt-run-plan-error" role="alert" style={{ marginTop: 12 }}>
            {error}
          </div>
        ) : null}
      </RtModalSection>
    </RtModal>
  );
};

export default RunTestPlanModal;
